import React, { useEffect } from 'react';
import Spinner from '@atlaskit/spinner';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { UnloadPrompt } from '@atlassian/jira-common-components-unload-prompt';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	TranslationPanel,
	Language,
} from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import PopoutPanelNav4 from '@atlassian/jira-servicedesk-popout-panel-nav4/src/ui/index.tsx';
import type { ProjectId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	useTranslationController,
	useSelectedPanel,
	useFormDirty,
} from '../controllers/manage-translations/index.tsx';
import { ContentPreview } from './content-preview';
import { EditDisabledBanner } from './edit-disabled-banner';
import { EmailTemplatePanel } from './email-template-panel';
import { GenericPanel } from './generic-panel';
import { GlobalObjectPanel } from './global-objects-panel';
import { Footer } from './manage-translations-page-footer';
import messages from './messages';
import { PreviewSendTestEmailActions } from './preview-send-test-email-actions';
import { SendMeATestEmailButton } from './send-me-a-test-email';
import { SidebarV4TranslationsPanel } from './sidebar-v4-translations';
import { UnsavedChangesModal } from './unsaved-changes-modal';

const SelectedPanel = ({
	selectedPanel,
	projectId,
	projectKey,
	defaultLanguage,
	selectedLanguage,
}: {
	selectedPanel: TranslationPanel;
	projectId: ProjectId;
	projectKey: ProjectKey;
	defaultLanguage: Language;
	selectedLanguage: Language;
}) => {
	switch (selectedPanel.providerUrlKey) {
		case 'notification-template':
			return (
				<EmailTemplatePanel
					projectId={projectId}
					projectKey={projectKey}
					defaultLanguage={defaultLanguage}
					selectedLanguage={selectedLanguage}
					translationPanel={selectedPanel}
					ContentPreview={ContentPreview}
					SendMeATestEmail={SendMeATestEmailButton}
				/>
			);
		case 'global-objects':
			return <GlobalObjectPanel />;
		default:
			return (
				<GenericPanel
					projectId={projectId}
					projectKey={projectKey}
					translationPanel={selectedPanel}
					selectedLanguage={selectedLanguage}
					defaultLanguage={defaultLanguage}
					ContentPreview={ContentPreview}
					SendMeATestEmailButton={SendMeATestEmailButton}
					PreviewSendTestEmailActions={PreviewSendTestEmailActions}
				/>
			);
	}
};

export const ManageTranslationsPage = ({
	projectId,
	projectKey,
}: {
	projectId: ProjectId;
	projectKey: ProjectKey;
}) => {
	const [state, actions, error, loading, refresh] = useTranslationController({
		canInitializeStore: true,
	});

	const selectedPanel = useSelectedPanel();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFormDirty = useFormDirty();
	const isAdmin = useIsAdmin();
	const { formatMessage } = useIntl();

	useEffect(() => {
		fireOperationalAnalytics(createAnalyticsEvent({}), 'manageTranslationsPage loaded');
	}, [createAnalyticsEvent]);

	useEffect(
		() => () => {
			actions.resetSelectedLanguage();
			refresh();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	if (loading) {
		return <Spinner />;
	}

	if (error) {
		throw error;
	}

	if (!selectedPanel || !state.defaultLanguage || !state.selectedLanguage) {
		return null;
	}

	const translationFields = selectedPanel?.translationGroups[0]?.translationFields;
	const hasMultipleTranslationFields =
		translationFields &&
		translationFields.length > 1 &&
		translationFields[0].inputType === 'notification-editor';

	const showEditDisabledBanner = selectedPanel?.editDisabled;

	if (getWillShowNav4()) {
		return (
			<>
				<PageHeader>
					{formatMessage(messages.header, { language: state.selectedLanguage.label })}
				</PageHeader>
				<UnloadPrompt beforeUnload={isFormDirty} />
				<PopoutPanelNav4
					isPoppedOut
					popoutComponent={<SidebarV4TranslationsPanel projectKey={projectKey} />}
				>
					{selectedPanel.providerUrlKey === 'notification' && !hasMultipleTranslationFields && (
						<PreviewSendTestEmailActions
							projectKey={projectKey}
							translationPanel={selectedPanel}
							defaultLanguage={state.defaultLanguage}
							selectedLanguage={state.selectedLanguage}
							ContentPreview={ContentPreview}
							SendMeATestEmail={SendMeATestEmailButton}
						/>
					)}

					{showEditDisabledBanner && <EditDisabledBanner />}
					<SelectedPanel
						projectId={projectId}
						projectKey={projectKey}
						selectedPanel={selectedPanel}
						defaultLanguage={state.defaultLanguage}
						selectedLanguage={state.selectedLanguage}
					/>

					{selectedPanel.providerUrlKey === 'sd-simplified-request-type' &&
						(isAdmin ? (
							<Link href="/secure/admin/ViewStatuses.jspa">
								{formatMessage(messages.workflowStatus)}
							</Link>
						) : (
							<>{formatMessage(messages.workflowStatusNonAdminHelp)}</>
						))}

					<Footer />
					<UnsavedChangesModal />
				</PopoutPanelNav4>
			</>
		);
	}

	return (
		<>
			<PageHeader>{selectedPanel.displayName}</PageHeader>
			<UnloadPrompt beforeUnload={isFormDirty} />
			{selectedPanel.providerUrlKey === 'notification' && !hasMultipleTranslationFields && (
				<PreviewSendTestEmailActions
					projectKey={projectKey}
					translationPanel={selectedPanel}
					defaultLanguage={state.defaultLanguage}
					selectedLanguage={state.selectedLanguage}
					ContentPreview={ContentPreview}
					SendMeATestEmail={SendMeATestEmailButton}
				/>
			)}

			{showEditDisabledBanner && <EditDisabledBanner />}
			<SelectedPanel
				projectId={projectId}
				projectKey={projectKey}
				selectedPanel={selectedPanel}
				defaultLanguage={state.defaultLanguage}
				selectedLanguage={state.selectedLanguage}
			/>

			{selectedPanel.providerUrlKey === 'sd-simplified-request-type' &&
				(isAdmin ? (
					<Link href="/secure/admin/ViewStatuses.jspa">
						{formatMessage(messages.workflowStatus)}
					</Link>
				) : (
					<>{formatMessage(messages.workflowStatusNonAdminHelp)}</>
				))}

			<Footer />
			<UnsavedChangesModal />
		</>
	);
};
